import { messages } from "./messages";
import { Environments, ApiEnvironments, SaveFlows } from "./constants";

const CustomizationFrameWrapperInternal = ({ onConfigurationChanged, onSessionSavedAsync, targetDiv, sku, saveFlow, initialConfiguration, configurationId, locale, apiEnvironment, allowFullscreen, showHeader, trace }) => {
  let currentFrame;
  let registeredCallbacks = {};

  const sendMessage = (event, data, correlationId) => {
    if (currentFrame?.contentWindow) {
      currentFrame.contentWindow.postMessage(
        {
          source: "met-configurator-frame",
          correlationId: correlationId,
          payload: {
            event: event,
            data: data,
          },
        },
        "*"
      );
    }
  };
  const registerCallback = (event, correlationId, callback) => {
    registeredCallbacks[`${event}.${correlationId}`] = callback;
  };
  const clearCallback = (event, correlationId) => {
    delete registeredCallbacks[`${event}.${correlationId}`];
  };
  const callCallback = (event, correlationId, data) => {
    if (registeredCallbacks[`${event}.${correlationId}`]) {
      registeredCallbacks[`${event}.${correlationId}`](data);
    }
    clearCallback(event, correlationId);
  };

  const messageListener = (event) => {
    if (event.data.source === "met-configurator") {
      switch (event.data.payload.event) {
        case messages.configurationChanged:
          if (onConfigurationChanged) {
            onConfigurationChanged(event.data.payload.data)
          }
          break
        case messages.sessionSaved:
          handleSessionSaved(event.data.payload.data, event.data.correlationId);
          break;
        default:
          callCallback(event.data.source, event.data.correlationId);
          console.log("Unknown message", event);
      }
    }
  };

  const handleSessionSaved = (data, correlationId) => {
    if (onSessionSavedAsync) {
      onSessionSavedAsync(data)
        .then((response) => {
          sendMessage(messages.sessionSavedSuccess, response, correlationId);
        })
        .catch((err) => {
          sendMessage(messages.sessionSavedError, err, correlationId);
        });
    } else {
      sendMessage(messages.sessionSavedSuccess, "", correlationId);
    }
  };

  const init = () => {
    if (!targetDiv) {
      throw new Error("targetDiv is required");
    }

    if (currentFrame) {
      destroy();
    }

    window.addEventListener("message", messageListener);
    currentFrame = document.createElement("iframe");
    const currentFrameId = "met-frame-" + new Date().valueOf();

    const params = [];

    let url;
    switch (apiEnvironment?.toLowerCase()) {
      case ApiEnvironments.dev:
        url = `https://custom-dev.milwaukeetool.com/external/ThreeKitFrame`;
        break;
      case ApiEnvironments.test:
        url = `https://custom-test.milwaukeetool.com/external/ThreeKitFrame`;
        break;
      case ApiEnvironments.oxygen:
        url = `https://custom-oxygen.milwaukeetool.com/external/ThreeKitFrame`;
        break;
      case ApiEnvironments.nitrogen:
        url = `https://custom-nitrogen.milwaukeetool.com/external/ThreeKitFrame`;
        break;
      case ApiEnvironments.stage:
        url = `https://custom-stage.milwaukeetool.com/external/ThreeKitFrame`;
        break;
      case ApiEnvironments.production:
      default:
        url = `https://custom.milwaukeetool.com/external/ThreeKitFrame`;
        break;
    }

    if (sku) {
      params.push(`sku=${sku}`);
    }
    if (configurationId) {
      params.push(`cid=${configurationId}`)
    }
    if (trace) {
      params.push(`t=1`);
    }
    if (initialConfiguration) {
      params.push("ic=" + encodeURIComponent(JSON.stringify(initialConfiguration)));
    }
    if (locale) {
      params.push("l=" + locale);
    }
    if (saveFlow) {
      params.push("sf=" + saveFlow?.toLowerCase());
    }
    params.push(`sh=${showHeader ? 1 : 0}`);

    if (params.length) {
      url += "?" + params.join("&");
    }

    currentFrame.src = url;
    currentFrame.id = currentFrameId;
    currentFrame.allowFullscreen = allowFullscreen;
    if (allowFullscreen) {
      currentFrame.allow = "fullscreen";
    }
    currentFrame.scrolling = "auto";
    currentFrame.style.display = "block";
    currentFrame.style.flexGrow = "1";
    currentFrame.style.border = "none";
    currentFrame.style.margin = "0";
    currentFrame.style.padding = "0";
    currentFrame.style.overflow = "visible";

    targetDiv.appendChild(currentFrame);
  };

  const destroy = () => {
    window.removeEventListener("message", messageListener);
    if (currentFrame) {
      currentFrame.parentNode.removeChild(currentFrame);
      currentFrame = undefined;
    }
    registeredCallbacks = {};
  };

  init();

  return {
    destroy,
  };
};

class CustomizationFrameWrapper {
  /**
   *
   * @param {function} onConfigurationChanged - called whenever the current configuration values change.
   * @param {function} onSessionSavedAsync - called at completion of the customization session (when adding to cart). This should return a promise.
   * @param {node} targetDiv - The div to create the iframe within.* @param {string} [marketingSku] - The marketing group to load in the visualizer, currently only HEAD_PROTECTION and VEST supported.
   * @param {string} [sku] - The Milwaukee Tool sku to load in the visualizer.
   * @param {SaveFlows|string} [saveFlow=SaveFlows.cart] - Controls the save flow, SaveFlows.cart will require a onSessionSavedAsync callback, SaveFlows.email will enable the user to email the customization to themselves.
   * @param {Object} [initialConfiguration] - Preload the customization with the provided values.
   * @param {string} [configurationId] - The configuration id to load into the visualizer.
   * @param {string} [locale] - The locale for the visualizer, currently only en-us is supported.
   * @param {Environments|string} [environment=Environments.production] - Controls the environment being used.  Deprecated, use apiEnvironment instead.
   * @param {ApiEnvironments|string} [apiEnvironment=ApiEnvironments.production] - Controls the api environment being used.
   * @param {boolean} [allowFullscreen=true] - Allow ThreeKit viewer to go fullscreen.
   * @param {boolean} [showHeader=true] - Show header portion of the visualizer.
   * @param {boolean} [trace=false] - Show debugging messages to console.
   * @returns {{destroy: function}} - Function to destroy the instance.
   */
  static init({
    onSessionSavedAsync,
    onConfigurationChanged,
    targetDiv,
    sku,
    saveFlow = SaveFlows.cart,
    initialConfiguration,
    configurationId,
    locale,
    environment = Environments.production,
    apiEnvironment = ApiEnvironments.production,
    allowFullscreen = true,
    showHeader = true,
    trace = false,
  }) {
    if (environment === Environments.preview && apiEnvironment === ApiEnvironments.production) {
      apiEnvironment = ApiEnvironments.stage; //for MT.com backwards compatibility
    }

    return CustomizationFrameWrapperInternal({
      onSessionSavedAsync,
      onConfigurationChanged,
      targetDiv,
      sku,
      saveFlow,
      initialConfiguration,
      configurationId,
      locale,
      apiEnvironment,
      allowFullscreen,
      showHeader,
      trace,
    });
  }
}

export default CustomizationFrameWrapper;
